import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'

export interface MarketingSidebarItem {
  id: string
  title: string
  href: string
  icon: keyof typeof Icons
  badge?: React.ReactNode
}

export interface MarketingSidebarSection {
  title: string
  items: MarketingSidebarItem[]
  defaultExpanded?: boolean
}

interface MarketingSidebarProps {
  className?: string
  locked?: boolean
}

const sidebarSections: MarketingSidebarSection[] = [
  {
    title: 'Create',
    defaultExpanded: true,
    items: [
      { id: 'ad-generator', title: 'Ad Generator', href: '/marketing/ad-generator', icon: 'Wand2' },
    ],
  },
  {
    title: 'Research',
    defaultExpanded: true,
    items: [
      { id: 'competitor-analysis', title: 'Competitor Analysis', href: '/marketing/competitor-analysis', icon: 'Search' },
      { id: 'tiktok-intel', title: 'TikTok Intelligence', href: '/marketing/tiktok-intel', icon: 'Globe' },
      { id: 'ads', title: 'Ads', href: '/marketing/research/ads', icon: 'Sparkles' },
      { id: 'saved-ads', title: 'Saved Ads', href: '/marketing/research/saved-ads', icon: 'Bookmark' },
      // Brands hidden for now
    ],
  },
  {
    title: 'AI Playground',
    defaultExpanded: true,
    items: [
      // Swipe hidden for now
      { id: 'storyboard', title: 'Storyboard', href: '/marketing/playground/storyboard', icon: 'LayoutGrid' },
      { id: 'storyboard-generator', title: 'AI Storyboard', href: '/marketing/playground/storyboard-generator', icon: 'Sparkles' },
      // Ad Remix hidden for now
      // Creator Evaluator hidden for now
    ],
  },
  {
    title: 'AI Chats',
    defaultExpanded: true,
    items: [
      { id: 'marketing-chats', title: 'Marketing Chats', href: '/marketing/chats', icon: 'MessageSquare' },
      { id: 'storyboard-replacement', title: 'Storyboard Replacement', href: '/marketing/chats/storyboard-replacement', icon: 'Clapperboard' },
    ],
  },
  {
    title: 'Repository',
    defaultExpanded: true,
    items: [
      { id: 'repository-videos', title: 'Company Videos', href: '/marketing/repository/footage', icon: 'Video' },
      { id: 'repository-creators', title: 'Creators', href: '/marketing/repository/creators', icon: 'Users' },
    ],
  },
  {
    title: 'Debug',
    defaultExpanded: true,
    items: [
      { id: 'debug-queue', title: 'Queue Debug', href: '/marketing/debug/queue', icon: 'Activity' },
      { id: 'debug-videos', title: 'Video Debug', href: '/marketing/debug/videos', icon: 'MonitorPlay' },
      ...(localStorage.getItem('dev_only') === 'true'
        ? [
            { id: 'brand-context', title: 'Brand Context', href: '/marketing/debug/brand-context', icon: 'Settings2' as keyof typeof Icons },
            { id: 'prompt-engineering', title: 'Prompt Engineering', href: '/marketing/debug/prompt-engineering', icon: 'FileText' as keyof typeof Icons },
          ]
        : []),
    ],
  },
]

export function MarketingSidebar({ className, locked = false }: MarketingSidebarProps) {
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    sidebarSections.reduce((acc, section) => {
      acc[section.title] = section.defaultExpanded ?? true
      return acc
    }, {} as Record<string, boolean>)
  )
  const [collapsed, setCollapsed] = useState(false)

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const toggleSection = (sectionTitle: string) => {
    if (collapsed) return
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }))
  }

  return (
    <aside
      className={cn(
        'bg-tf-bg-muted border-r border-tf-border transition-all duration-300 flex flex-col min-h-screen',
        collapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'h-14 flex items-center border-b border-tf-border',
          collapsed ? 'px-3 justify-center' : 'px-4'
        )}
      >
        <Link to="/marketing" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-[#005BD3] flex items-center justify-center flex-shrink-0">
            <Icons.Zap className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <span className="text-base font-semibold text-tf-text">Marketing</span>
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="ml-auto p-1.5 rounded hover:bg-tf-bg-hover transition-colors"
          >
            <Icons.PanelLeftClose className="w-4 h-4 text-tf-text-muted" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {/* Back to Store */}
        <div className="mb-4">
          <Link
            to="/"
            title={collapsed ? "Back to Store" : undefined}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded transition-all text-sm font-medium text-tf-text hover:bg-tf-bg-hover group'
            )}
          >
            <Icons.ArrowLeft className="w-5 h-5 flex-shrink-0 group-hover:-translate-x-1 transition-transform" />
            {!collapsed && (
              <span className="flex-1 truncate">Back to Store</span>
            )}
          </Link>
        </div>

        {sidebarSections.map((section) => {
          const isExpanded = expandedSections[section.title]

          return (
            <div key={section.title} className="mb-4">
              {/* Section Header */}
              {!collapsed && section.title && (
                <button
                  onClick={() => toggleSection(section.title)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 mb-1.5 text-xs font-semibold uppercase tracking-wide text-tf-text-muted hover:text-tf-text transition-colors'
                  )}
                >
                  <span>{section.title}</span>
                  <Icons.ChevronDown
                    className={cn(
                      'w-3 h-3 ml-auto transition-transform',
                      !isExpanded && '-rotate-90'
                    )}
                  />
                </button>
              )}

              {/* Section Items */}
              {(isExpanded || collapsed) && (
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const IconComponent = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[item.icon] || Icons.Circle
                    const active = isActive(item.href)

                    // When locked, render as non-clickable div
                    if (locked) {
                      return (
                        <div
                          key={item.id}
                          title={collapsed ? item.title : undefined}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 rounded text-sm font-medium',
                            'text-tf-text-muted opacity-50 cursor-not-allowed'
                          )}
                        >
                          <IconComponent className="w-5 h-5 flex-shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1 truncate">{item.title}</span>
                              <Icons.Lock className="w-3.5 h-3.5 flex-shrink-0" />
                            </>
                          )}
                        </div>
                      )
                    }

                    return (
                      <Link
                        key={item.id}
                        to={item.href}
                        title={collapsed ? item.title : undefined}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded transition-all text-sm font-medium',
                          active
                            ? 'bg-[#005BD3] text-white shadow-sm'
                            : 'text-tf-text hover:bg-tf-bg-hover'
                        )}
                      >
                        <IconComponent className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate">{item.title}</span>
                            {item.badge && (
                              <span className="flex-shrink-0">{item.badge}</span>
                            )}
                          </>
                        )}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="p-2 border-t border-tf-border">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-tf-text hover:bg-tf-bg-hover transition-all text-sm font-medium"
        >
          {collapsed ? (
            <Icons.ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <Icons.ChevronLeft className="w-4 h-4" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  )
}

export default MarketingSidebar
